import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { Copy, ExternalLink, Heading1 } from "lucide-react";
import type { PageIssue, DuplicateH1Details } from "@/types/technicalAudit";

interface DuplicateH1CardProps {
	issue: PageIssue;
}

export function DuplicateH1Card({ issue }: DuplicateH1CardProps) {
	const details = issue.issue_details as DuplicateH1Details;

	return (
		<div className="space-y-4">
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center">
							<Copy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
						</div>
						Duplicated H1
						<Badge variant="outline" className="ml-auto bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
							{details.count} pages
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<blockquote className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-4 rounded-r-lg">
						<p className="text-sm font-medium text-amber-900 dark:text-amber-300 italic">
							&ldquo;{details.h1_text}&rdquo;
						</p>
					</blockquote>
				</CardContent>
			</Card>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Heading1 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Pages Using This H1
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<div className="space-y-2">
						{details.duplicate_urls.map((url) => (
							<a
								key={url}
								href={url}
								onClick={(e) => openWithCacheBuster(e, url)}
								target="_blank"
								rel="noopener noreferrer"
								className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
							>
								<p className="text-sm font-mono break-all text-foreground flex-1">{url}</p>
								<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
							</a>
						))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
